import React, {useContext, useState, useEffect, useRef, useCallback} from 'react'
import {useLocation} from 'react-router-dom'
import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui'
import PropTypes from 'prop-types'
import loadable from '@loadable/component'

// Lazy load MiniCart - only needed on desktop when user clicks cart icon
const MiniCart = loadable(() => import('../components/mini-cart'), {
    ssr: false
})

/**
 * This is the context for managing the MiniCart.
 * Used in top level App component.
 */
export const MiniCartContext = React.createContext()
export const useMiniCartContext = () => useContext(MiniCartContext)
export const MiniCartProvider = ({children}) => {
    const miniCart = useMiniCart()
    // useBreakpointValue returns undefined during SSR, false on mobile, true on desktop
    // MiniCart only renders client-side on desktop (mobile goes directly to bag page)
    const isDesktop = useBreakpointValue({base: false, lg: true})

    // Listen for the GDI drawer-link event so shortcode-driven mini-cart links
    // open the MiniCart drawer.
    useEffect(() => {
        if (typeof window === 'undefined') return undefined
        const handler = (e) => {
            if (e?.detail?.drawer === 'mini-cart') miniCart.onOpen()
        }
        window.addEventListener('gdi:openDrawer', handler)
        return () => window.removeEventListener('gdi:openDrawer', handler)
    }, [miniCart.onOpen])

    return (
        <MiniCartContext.Provider value={miniCart}>
            {children}
            {isDesktop && <MiniCart />}
        </MiniCartContext.Provider>
    )
}
MiniCartProvider.propTypes = {
    children: PropTypes.node.isRequired
}

export const useMiniCart = () => {
    const [isOpen, setIsOpen] = useState(false)
    const isHoveredRef = useRef(false)
    const pendingCloseRef = useRef(false)

    const {pathname} = useLocation()
    useEffect(() => {
        if (isOpen) {
            pendingCloseRef.current = false
            setIsOpen(false)
        }
    }, [pathname])

    const onClose = useCallback(() => {
        pendingCloseRef.current = false
        setIsOpen(false)
    }, [])

    // Hover-aware close used by the auto-close timer after add-to-cart.
    // If the user is hovering the drawer, defer closing until mouse leaves.
    const onAutoClose = useCallback(() => {
        if (isHoveredRef.current) {
            pendingCloseRef.current = true
            return
        }
        pendingCloseRef.current = false
        setIsOpen(false)
    }, [])

    const onMouseEnter = useCallback(() => {
        isHoveredRef.current = true
    }, [])

    const onMouseLeave = useCallback(() => {
        isHoveredRef.current = false
        if (pendingCloseRef.current) {
            pendingCloseRef.current = false
            setIsOpen(false)
        }
    }, [])

    return {
        isOpen,
        onOpen: () => {
            pendingCloseRef.current = false
            setIsOpen(true)
        },
        onClose,
        onAutoClose,
        onMouseEnter,
        onMouseLeave
    }
}
